import { z } from 'zod';

import { ZodAmount } from '@Common/types/Amount';
import {
  B2bSavingPlanCompartmentValues,
  ZodB2bSavingPlanCompartment,
} from '@Common/types/b2b/compartments';
import { ZodB2bSavingPlanType } from '@Common/types/b2b/plans';
import { ZodEntryFee } from '@Common/types/explore/mutual_fund_order';
import {
  ZodInternalSavingPlanId,
  ZodProfileSavingPlanId,
} from '@Common/types/profile/saving_plans';
import { ZodTransactionOrderMove } from '@Common/types/profile/transactions/enums';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';

export const ZodArbitrageAllocationSchema = z.object({
  instrument_id: z.number().int().nullable(),
  amount: z.number(),
  saving_plan_id: ZodProfileSavingPlanId,
});

export type ArbitrageAllocationSchema = z.infer<typeof ZodArbitrageAllocationSchema>;

export const ZodArbitrageCompartmentEntry = z.object({
  compartment: ZodB2bSavingPlanCompartment.nullish(),
  sources: ZodArbitrageAllocationSchema.array().min(1),
  targets: ZodArbitrageAllocationSchema.array().min(1),
});

export type ArbitrageCompartmentEntry = z.infer<typeof ZodArbitrageCompartmentEntry>;

const ARBITRAGE_ENTRIES_DESCRIPTION =
  'One independent arbitrage per compartment. Each compartment may appear at most once. ' +
  'compartment is nullish for non-compartmentalized products or prev version calls that ' +
  "don't send it; at most one such entry (without a compartment) may be present.";

export const refineArbitrageCompartmentEntries = (
  arbitrages: Pick<ArbitrageCompartmentEntry, 'compartment'>[],
  ctx: z.RefinementCtx,
) => {
  const compartments = arbitrages.map((entry) => entry.compartment ?? null);

  const withoutCompartment = compartments.filter((compartment) => compartment === null).length;
  if (withoutCompartment > 1) {
    ctx.addIssue({
      code: 'custom',
      path: ['arbitrages'],
      message: 'only one arbitrage without a compartment is allowed',
    });
    return;
  }

  if (new Set(compartments).size !== compartments.length) {
    ctx.addIssue({
      code: 'custom',
      path: ['arbitrages'],
      message: 'each compartment may appear at most once',
    });
  }
};

const ZodMutualFundArbitrageBodyBase = z.object({
  arbitrages: ZodArbitrageCompartmentEntry.array()
    .min(1)
    .max(B2bSavingPlanCompartmentValues.length)
    .describe(ARBITRAGE_ENTRIES_DESCRIPTION),
  agreed_terms: z.enum(['key_information_documents']).array(),
});

export const ZodMutualFundArbitrageBody = ZodMutualFundArbitrageBodyBase.superRefine((body, ctx) =>
  refineArbitrageCompartmentEntries(body.arbitrages, ctx),
);

export type MutualFundArbitrageBody = z.infer<typeof ZodMutualFundArbitrageBody>;

export const postExploreMutualFundArbitrageEndpoint = endpoint({
  method: 'POST',
  body: ZodMutualFundArbitrageBody,
  responseBody: z.object({
    success: z.boolean(),
    is_order_confirmed: z.boolean(),
  }),
});

export type PostExploreMutualFundArbitrageEndpoint = GetEndpointType<
  typeof postExploreMutualFundArbitrageEndpoint
>;

export const ZodArbitrageFullAllocation = z // should include all positions from current pilote if targeted
  .object({
    saving_plan_id: ZodProfileSavingPlanId,
    saving_plan_type: ZodB2bSavingPlanType,
    instrument_id: z.number(),
    amount_gross: ZodAmount, // Amount should positive destination and negative for source
    entry_fee: ZodEntryFee.nullable(),
    side: ZodTransactionOrderMove,
  });

export type ArbitrageFullAllocation = z.infer<typeof ZodArbitrageFullAllocation>;

const ZodMutualFundArbitrageSummaryBodyBase = z.object({
  arbitrages: ZodArbitrageCompartmentEntry.array()
    .min(1)
    .max(B2bSavingPlanCompartmentValues.length)
    .describe(ARBITRAGE_ENTRIES_DESCRIPTION),
});

export const ZodMutualFundArbitrageSummaryBody = ZodMutualFundArbitrageSummaryBodyBase.superRefine(
  (body, ctx) => refineArbitrageCompartmentEntries(body.arbitrages, ctx),
);

export type MutualFundArbitrageSummaryBody = z.infer<typeof ZodMutualFundArbitrageSummaryBody>;

export const ZodMutualFundArbitrageSummaryResult = z.object({
  allocations: ZodArbitrageFullAllocation.array(),
  amount_gross: ZodAmount,
  amount_net: ZodAmount, // gross + entry fee
  entry_fee: ZodAmount,
});

export const postExploreMutualFundArbitrageSummaryEndpoint = endpoint({
  method: 'POST',
  body: ZodMutualFundArbitrageSummaryBody,
  responseBody: z.object({
    arbitrages: z
      .object({
        compartment: ZodB2bSavingPlanCompartment.nullable(),
        ...ZodMutualFundArbitrageSummaryResult.shape,
      })
      .array()
      .describe(
        'One entry per requested arbitrage, in request order. ' +
          'compartment is null for the non-compartmentalised entry, if any.',
      ),
  }),
});

export type PostExploreMutualFundArbitrageSummaryEndpoint = GetEndpointType<
  typeof postExploreMutualFundArbitrageSummaryEndpoint
>;

const ZodDestinationRestrictionSource = z.object({
  instrument_id: z.number().int(),
  plan_id: ZodInternalSavingPlanId,
});

export const postExploreMutualFundArbitrageDestinationRestrictionsEndpoint = endpoint({
  method: 'POST',
  body: z.object({
    source_instrument_ids: ZodDestinationRestrictionSource.array().min(1),
  }),
  responseBody: z.object({
    restricted: z.array(
      z.object({
        sourceInstrumentId: z.number().int(),
        sourcePlanId: ZodInternalSavingPlanId,
        targetInstrumentId: z.number().int(),
        targetPlanId: ZodInternalSavingPlanId,
      }),
    ),
  }),
});

export type PostExploreMutualFundArbitrageDestinationRestrictionsEndpoint = GetEndpointType<
  typeof postExploreMutualFundArbitrageDestinationRestrictionsEndpoint
>;
